'use client'

import { Alert, Button, Card, ConfirmDialog, Stack, Typography } from '@org/design-system'
import { PluginRegistry } from '@org/runtime-engine'
import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { Profiler, type ReactNode, useEffect, useRef, useState } from 'react'
import { DialogRenderer } from './DialogRenderer'
import { FormRenderer } from './FormRenderer'
import { TableRenderer } from './TableRenderer'
import { RoleGate } from './auth/RoleGate'
import { PlanGate } from './billing/PlanGate'
import { ActiveUsersTopbar } from './collaboration/ActiveUsersTopbar'
import { CollaborationCursors } from './collaboration/CollaborationCursors'
import { PresenceProvider } from './collaboration/PresenceProvider'
import { useCollaborationPresence } from './collaboration/useCollaborationPresence'
import { useRenderMetrics } from './hooks/useRenderMetrics'
import { AnalyticsGridRenderer } from './layouts/AnalyticsGridRenderer'
import { TabsRenderer } from './layouts/TabsRenderer'
import { getRegisteredLayout } from './plugin-runtime'
import type {
  RuntimeUserRole,
  ShadcnDashboardWidget,
  ShadcnLayoutType,
  ShadcnPageConfig,
  ShadcnPageType,
  ShadcnPlan,
} from './types'

interface PageRendererProps {
  page: ShadcnPageConfig
  apiBaseUrl?: string
  roles?: Record<string, RuntimeUserRole>
  auth?: ShadcnPageConfig['auth']
  appId?: string
  plan?: ShadcnPlan
  billingPortalUrl?: string
}

interface PageCompositionProps {
  pageType: ShadcnPageType
  entity?: string
  apiBaseUrl?: string
  auth?: ShadcnPageConfig['auth']
  page: ShadcnPageConfig
  appId?: string
}

interface LayoutRendererProps extends PageCompositionProps {
  layoutType: ShadcnLayoutType
}

function LayoutErrorFallback({ error }: { error: Error }) {
  return (
    <Stack gap={4}>
      <Alert variant="error" title="Layout render failed" description={error.message} />
      <Button variant="outline" onClick={() => window.location.reload()}>
        Reload page
      </Button>
    </Stack>
  )
}

function LayoutErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary fallback={({ error }) => <LayoutErrorFallback error={error} />}>
      {children}
    </Sentry.ErrorBoundary>
  )
}

function BasePageComposition({
  page,
  pageType,
  apiBaseUrl = '',
  appId = 'runtime-shell',
}: PageCompositionProps) {
  const [rowPendingDelete, setRowPendingDelete] = useState<Record<string, unknown> | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const { bindCursorTracking, setSelectedRowId } = useCollaborationPresence()

  useEffect(() => bindCursorTracking(wrapperRef.current), [bindCursorTracking])

  if (pageType === 'crud-list') {
    if (!page.table || !page.form) {
      return null
    }

    return (
      <div ref={wrapperRef} className="relative">
        <Stack gap={6}>
          <DialogRenderer
            config={{
              title: page.dialog?.title ?? `Create ${page.title ?? 'record'}`,
              description: page.dialog?.description,
              triggerLabel: page.dialog?.triggerLabel ?? 'Create',
              triggerVariant: page.dialog?.triggerVariant ?? 'primary',
            }}
            presenceRowId={null}
          >
            <FormRenderer config={page.form} />
          </DialogRenderer>

          <TableRenderer
            config={page.table}
            onRowFocus={(rowId) => setSelectedRowId(rowId)}
            rowActions={
              page.deleteAction
                ? (row) => (
                    <Button variant="danger" size="sm" onClick={() => setRowPendingDelete(row)}>
                      Delete
                    </Button>
                  )
                : undefined
            }
          />

          <ConfirmDialog
            isOpen={Boolean(rowPendingDelete)}
            onClose={() => setRowPendingDelete(null)}
            title={page.deleteAction?.title ?? 'Delete item?'}
            description={page.deleteAction?.description ?? 'This action cannot be undone.'}
            confirmLabel={page.deleteAction?.confirmLabel ?? 'Delete'}
            cancelLabel={page.deleteAction?.cancelLabel ?? 'Cancel'}
            variant="danger"
            onConfirm={() => {
              if (rowPendingDelete) {
                page.deleteAction?.onConfirm?.(rowPendingDelete)
              }
              setRowPendingDelete(null)
            }}
          />
          <CollaborationCursors />
        </Stack>
      </div>
    )
  }

  if (pageType === 'tabs' && page.tabs) {
    return (
      <TabsRenderer
        tabs={page.tabs}
        apiBaseUrl={apiBaseUrl}
        renderPage={(tabPage) => (
          <PageRenderer page={tabPage} apiBaseUrl={apiBaseUrl} appId={appId} />
        )}
      />
    )
  }

  if (pageType === 'dashboard' && page.widgets) {
    return <AnalyticsGridRenderer apiBaseUrl={apiBaseUrl} widgets={page.widgets} />
  }

  if (pageType === 'table' && page.table) {
    return (
      <div ref={wrapperRef} className="relative">
        <TableRenderer config={page.table} onRowFocus={(rowId) => setSelectedRowId(rowId)} />
        <CollaborationCursors />
      </div>
    )
  }

  if (pageType === 'form' && page.form) {
    return (
      <div ref={wrapperRef} className="relative">
        <FormRenderer config={page.form} />
        <CollaborationCursors />
      </div>
    )
  }

  if (pageType === 'dialog' && page.dialog && page.form) {
    return (
      <div ref={wrapperRef} className="relative">
        <DialogRenderer config={page.dialog} presenceRowId={null}>
          <FormRenderer config={page.form} />
        </DialogRenderer>
        <CollaborationCursors />
      </div>
    )
  }

  return null
}

function FullPageLayout(props: LayoutRendererProps) {
  return (
    <div className="w-full">
      <BasePageComposition {...props} />
    </div>
  )
}

function SplitPanelRenderer(props: LayoutRendererProps) {
  return (
    <LayoutErrorBoundary>
      <Card padding="md">
        <Stack gap={6}>
          <BasePageComposition {...props} />
        </Stack>
      </Card>
    </LayoutErrorBoundary>
  )
}

function KanbanRenderer(props: LayoutRendererProps) {
  return (
    <LayoutErrorBoundary>
      <Card padding="md">
        <Stack gap={6}>
          <BasePageComposition {...props} />
        </Stack>
      </Card>
    </LayoutErrorBoundary>
  )
}

function AnalyticsGridLayoutRenderer(props: LayoutRendererProps) {
  // Widgets may live at page.layout.widgets (engine schema) OR page.widgets (types.ts schema)
  const layoutWidgets = (props.page.layout as Record<string, unknown> | undefined)?.widgets as
    | Array<Record<string, unknown>>
    | undefined
  const pageWidgets = (props.page as unknown as { widgets?: ShadcnDashboardWidget[] }).widgets
  const rawWidgets = (layoutWidgets ?? pageWidgets) || []
  // Normalize: KV configs may use `widgetType` (SDK schema) while renderer expects `type`
  const widgets = rawWidgets.map((w) => ({
    ...w,
    type: ((w as Record<string, unknown>).type ??
      (w as Record<string, unknown>).widgetType) as string,
  })) as ShadcnDashboardWidget[]

  if (widgets.length > 0) {
    return (
      <LayoutErrorBoundary>
        <AnalyticsGridRenderer apiBaseUrl={props.apiBaseUrl ?? ''} widgets={widgets} />
      </LayoutErrorBoundary>
    )
  }

  // Fallback: delegate to BasePageComposition (handles dashboard with page.widgets, etc.)
  return (
    <LayoutErrorBoundary>
      <BasePageComposition {...props} />
    </LayoutErrorBoundary>
  )
}

function TabsLayoutRenderer(props: LayoutRendererProps) {
  return (
    <LayoutErrorBoundary>
      <Card padding="md">
        <Stack gap={6}>
          <BasePageComposition {...props} />
        </Stack>
      </Card>
    </LayoutErrorBoundary>
  )
}

function InstrumentedLayout({
  layoutType,
  page,
  children,
}: { layoutType: ShadcnLayoutType | 'flat'; page: ShadcnPageConfig; children: ReactNode }) {
  const onRender = useRenderMetrics(layoutType, {
    appId: page.id,
    pageId: page.path,
    pageType: page.type,
  })

  return (
    <Profiler id={`layout:${layoutType}`} onRender={onRender}>
      {children}
    </Profiler>
  )
}

function renderWithLayout(props: LayoutRendererProps): ReactNode {
  const frame = (children: ReactNode) => (
    <InstrumentedLayout layoutType={props.layoutType} page={props.page}>
      {children}
    </InstrumentedLayout>
  )

  switch (props.layoutType) {
    case 'kanban':
      return frame(<KanbanRenderer {...props} />)
    case 'analytics-grid':
      return frame(<AnalyticsGridLayoutRenderer {...props} />)
    case 'split-panel':
      return frame(<SplitPanelRenderer {...props} />)
    case 'tabs':
      return frame(<TabsLayoutRenderer {...props} />)
    case 'full':
    case 'full-page':
      return frame(<FullPageLayout {...props} />)
    default: {
      const PluginLayout = getRegisteredLayout(props.layoutType)
      if (PluginLayout) {
        return (
          <PluginLayout page={props.page}>
            <BasePageComposition {...props} />
          </PluginLayout>
        )
      }

      return <BasePageComposition {...props} />
    }
  }
}

export function PageRenderer({
  page,
  roles,
  auth,
  appId = 'runtime-shell',
  plan = 'free',
  billingPortalUrl,
}: PageRendererProps) {
  const queryClient = useQueryClient()
  const layoutType = page.layout?.type
  const registeredLayoutRenderer = layoutType ? PluginRegistry.resolve(layoutType) : null
  const renderProps: LayoutRendererProps = {
    layoutType: layoutType ?? 'split-panel',
    pageType: page.type,
    entity: page.entity,
    apiBaseUrl: page.apiBaseUrl,
    auth: page.auth,
    page,
    appId,
  }

  const pluginLayout = registeredLayoutRenderer?.schema.safeParse(page.layout)

  if (!pluginLayout?.success && registeredLayoutRenderer && import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(
      `[runtime-engine] Layout plugin "${registeredLayoutRenderer.type}" failed schema validation.`,
      pluginLayout?.error
    )
  }

  const pluginContent = (() => {
    if (!registeredLayoutRenderer || !pluginLayout?.success) {
      return null
    }

    const PluginComponent = registeredLayoutRenderer.component
    return (
      <PluginComponent
        page={page}
        apiBaseUrl={page.apiBaseUrl}
        auth={page.auth}
        queryClient={queryClient}
      />
    )
  })()

  const baseContent = layoutType ? (
    <PlanGate plan={plan} layoutType={layoutType} billingPortalUrl={billingPortalUrl}>
      {renderWithLayout(renderProps)}
    </PlanGate>
  ) : (
    <InstrumentedLayout layoutType="flat" page={page}>
      <BasePageComposition {...renderProps} />
    </InstrumentedLayout>
  )
  const requiredRole =
    page.layout && 'requiredRole' in page.layout ? page.layout.requiredRole : undefined
  const resolvedPageId = page.id ?? page.path ?? page.title ?? 'page'

  return (
    <PresenceProvider appId={appId} pageId={resolvedPageId}>
      <div className="relative">
        <div className="mb-3">
          <ActiveUsersTopbar />
        </div>
        {requiredRole ? (
          <RoleGate requiredRole={requiredRole} roles={roles} auth={auth}>
            {pluginContent ?? baseContent}
          </RoleGate>
        ) : (
          (pluginContent ?? baseContent)
        )}
        <CollaborationCursors />
        {import.meta.env.DEV ? (
          <Typography
            variant="caption"
            color="muted"
            className="pointer-events-none fixed bottom-3 left-3"
          >
            layout: {layoutType ?? 'flat'}
          </Typography>
        ) : null}
      </div>
    </PresenceProvider>
  )
}
